import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPage.css';

export const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="pp-root">
      <div className="pp-container">

        {/* Back nav */}
        <Link to="/" className="pp-back">← Back to Syndicator Showdown</Link>

        <div className="pp-header">
          <h1 className="pp-title">Privacy Policy &amp; General Disclaimer</h1>
          <p className="pp-meta">Last Updated: June 24, 2026 &nbsp;·&nbsp; Platform: Syndicator Showdown</p>
          <p className="pp-intro">
            This document governs your use of the Syndicator Showdown website, platform, and
            associated services. By accessing, registering an account, or participating in any
            Showdown, you agree to the terms outlined in this Privacy Policy and Disclaimer.
          </p>
        </div>

        {/* PART 1 */}
        <section className="pp-section">
          <h2 className="pp-part">Part 1: General Disclaimer</h2>

          <div className="pp-block">
            <h3 className="pp-heading">1. Nature of the Service (Not a Gambling Platform)</h3>
            <p>
              Syndicator Showdown is strictly a fantasy sports and virtual simulation game. All
              gameplay, including the drafting of real-world racehorses, the allocation of
              "bankrolls," the purchase of "shares" or "equity," and the awarding of "prize money,"
              is conducted entirely using virtual currency.
            </p>
            <ul className="pp-list">
              <li>
                <strong>No Real Money Wagering:</strong> Syndicator Showdown does not offer
                real-money gambling, sports betting, or wagering. Virtual bankrolls and prize money
                hold no real-world monetary value and cannot be withdrawn, cashed out, or exchanged
                for real currency.
              </li>
              <li>
                <strong>Not Real Ownership:</strong> Purchasing "shares" or "equity" within the
                platform does not confer any actual legal or financial ownership, rights, or
                entitlements to the real-world racehorses depicted.
              </li>
            </ul>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">2. Accuracy of Information &amp; Racing Data</h3>
            <p>
              While Syndicator Showdown utilises real-world racing data (including scratchings,
              race fields, and results) to power our fantasy mechanics, we do not guarantee the
              real-time accuracy, completeness, or reliability of this data. Delays, technical
              errors, or discrepancies between real-world official results and platform data may
              occur. The platform's ELO ranking system and internal data modelling are for
              entertainment purposes only.
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">3. Independence &amp; Affiliation</h3>
            <p>
              Syndicator Showdown is an independent fantasy platform. It is not affiliated with,
              endorsed by, or officially associated with any state or national racing authority,
              governing body, or any specific racing club.
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">4. Financial &amp; Investment Advice</h3>
            <p>
              Nothing contained on this platform constitutes financial, investment, or wagering
              advice. Past performance of real-world horses does not guarantee future results.
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">5. Age Restriction</h3>
            <p>
              You must be strictly 18 years of age or older to register an account and play
              Syndicator Showdown.
            </p>
          </div>
        </section>

        {/* PART 2 */}
        <section className="pp-section">
          <h2 className="pp-part">Part 2: Privacy Policy</h2>

          <div className="pp-block">
            <h3 className="pp-heading">1. Information We Collect</h3>
            <p>
              To provide you with the Syndicator Showdown experience, we collect the following
              types of information:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Account Data:</strong> Display name, email address, date of birth (for age
                verification), and authentication details (e.g., Google Auth credentials, encrypted
                Passkey/WebAuthn signatures).
              </li>
              <li>
                <strong>Gameplay Data:</strong> Your draft history, virtual transactions, stable
                selections, win/loss records, and ELO rankings.
              </li>
              <li>
                <strong>Device &amp; Usage Data:</strong> IP addresses, browser types, and
                interaction metrics to help us optimise the platform and troubleshoot technical
                issues.
              </li>
            </ul>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">2. How We Use Your Information</h3>
            <p>Your data is used strictly to facilitate and improve your experience on the platform:</p>
            <ul className="pp-list">
              <li>
                To operate and maintain your user dashboard, virtual wallet, and active Showdowns.
              </li>
              <li>
                To calculate and publicly display platform-wide metrics, including the Global
                Leaderboard and ELO player rankings.
              </li>
              <li>
                To securely authenticate your account (including via Face ID/Touch ID where enabled).
              </li>
              <li>
                To communicate important platform updates, account notifications, or responses to
                support enquiries.
              </li>
            </ul>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">3. Data Display &amp; Public Profile</h3>
            <p>
              By participating in a Showdown, you acknowledge that your chosen Display Name, along
              with your gameplay statistics, win history, and ELO rating, will be visible to other
              players on public leaderboards and game modules.
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">4. Data Security</h3>
            <p>
              We employ industry-standard security protocols to protect your personal information.
              Authentication and database architecture are powered by modern, secure cloud
              infrastructure. However, no method of transmission over the internet or electronic
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">5. Third-Party Services</h3>
            <p>
              We do not sell, rent, or trade your personal information to third parties. We may
              share necessary data with trusted service providers strictly for the purpose of
              operating the platform (e.g., database hosting, secure authentication providers).
            </p>
          </div>

          <div className="pp-block">
            <h3 className="pp-heading">6. Cookies and Tracking</h3>
            <p>
              Syndicator Showdown uses cookies and similar local storage mechanisms to keep you
              logged in, remember your preferences, and track session data (such as active Showdown
              states) to ensure seamless gameplay.
            </p>
          </div>
        </section>

        {/* PART 3 */}
        <section className="pp-section">
          <h2 className="pp-part">Part 3: Governing Law</h2>
          <div className="pp-block">
            <p>
              These terms, disclaimers, and privacy practices are governed by and construed in
              accordance with the laws of Queensland, Australia. Any disputes relating to these
              terms shall be subject to the exclusive jurisdiction of the courts of Queensland.
            </p>
          </div>
        </section>

        {/* Contact */}
        <div className="pp-contact">
          <h3 className="pp-contact-heading">Contact Us</h3>
          <p>
            For any questions regarding this Privacy Policy &amp; Disclaimer, or to request the
            deletion of your account and associated data, please contact us at:
          </p>
          <a href="mailto:admin@syndicatorshowdown.com.au" className="pp-contact-email">
            admin@syndicatorshowdown.com.au
          </a>
        </div>

        <div className="pp-footer-note">
          <Link to="/" className="pp-back">← Back to Syndicator Showdown</Link>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPage;
